import { ReactElement, useRef, useState } from 'react'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import toast from 'react-hot-toast'
import { useGenerateBlobSasUrlDelete, useGenerateBlobSasUrlUpload } from '../../api/upload-API'
import {
  deleteFileDirectFromBlob,
  uploadMultipleFilesWithConcurrency,
  type FileUpload,
  type UploadResult
} from '../../services/blobUploadService'
import Modal from '../Modal'
import { Button } from 'primereact/button'
import { useRecoilValue } from 'recoil'
import { currentGMIDState } from '../../utils/state-atoms'
import { UploadStatus } from '../../utils/constants'
import IconButton from '../buttons/IconButton'
import { CloseIcon, DeleteIcon } from '../Icons'

type FileData = {
  name: string
  path: string
  rawFile?: File
  progress?: number // 0-100
  uploading?: boolean
  failed?: boolean
  abortController?: AbortController // For managing individual file upload cancellation
}

type UploadProps = {
  label: string | ReactElement
  field: any
  fieldState: any
  setValue: (name: string, value: any) => void
  selectedFiles: FileData[]
  blobPrefix?: string
  uploadType: string
  disabled?: boolean
  isNew?: boolean
  uploadQuantity?: string
  setTempId?: (id: string) => void
  setSelectedFiles: (f: FileData[] | ((prev: FileData[]) => FileData[])) => void
  getFormErrorMessage: (name: string) => ReactElement | null
  /** Called immediately when an upload batch starts, with the raw Promise.
   *  Use this to register background-upload tracking before the modal closes. */
  onUploadPromiseCreated?: (promise: Promise<UploadResult[]>) => void
  /** Called on every progress tick (0-100) during upload. Fires even after the
   *  hosting component unmounts, so safe to wire into stores/callbacks. */
  onProgressUpdate?: (percent: number) => void
  /** Enable directory selection mode instead of individual files */
  allowDirectorySelection?: boolean
  accept?: string
  deleteConfirmationTarget?: string
  deletePersistedFilesFromStorage?: boolean
}

const Upload = ({
  label,
  field,
  fieldState,
  setValue,
  selectedFiles,
  blobPrefix,
  setSelectedFiles,
  getFormErrorMessage,
  uploadType,
  disabled,
  isNew,
  uploadQuantity = 'multiple',
  setTempId,
  onUploadPromiseCreated,
  onProgressUpdate,
  allowDirectorySelection = false,
  accept,
  deleteConfirmationTarget = 'Azure',
  deletePersistedFilesFromStorage = true
}: UploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [{}, generateSasUrlUpload] = useGenerateBlobSasUrlUpload()
  const [{}, generateSasUrlDelete] = useGenerateBlobSasUrlDelete()
  const [fileToDelete, setFileToDelete] = useState<FileData | null>(null)
  const [showModal, setShowModal] = useState(false)
  const gmid = useRecoilValue(currentGMIDState)

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Check if local storage has an entry for created timestamp. We use this to stage files "temporarily"
    let aeroTempRunId = localStorage.getItem('aero_temp_run_id')
    if (isNew && !aeroTempRunId) {
      // Create a new temp run id based on timestamp
      aeroTempRunId = `${Date.now()}`
      localStorage.setItem('aero_temp_run_id', aeroTempRunId)
      setTempId && setTempId(aeroTempRunId)
    }
    const files = event.target.files
    if (files && files.length > 0) {
      // For single file uploads, only allow 1 file
      if (uploadQuantity === 'single' && selectedFiles.length > 0) {
        toast.error('Only one file is allowed. Please remove the existing file first.', {
          duration: 4000
        })
        // Reset input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      const newFiles: FileData[] = []
      const duplicateFiles: string[] = []
      Array.from(files).forEach(file => {
        const isDuplicate = selectedFiles.some(f => f.name === file.name)
        if (isDuplicate) {
          duplicateFiles.push(file.name)
        } else {
          const filePath = (file as any).path || (file as any).webkitRelativePath || file.name
          const abortController = new AbortController()
          newFiles.push({
            name: file.name,
            path: filePath,
            rawFile: file as File,
            progress: 0,
            uploading: true,
            abortController: abortController
          })
        }
      })

      // For single file upload, only take the first file
      const filesToAdd = uploadQuantity === 'single' ? newFiles.slice(0, 1) : newFiles
      setSelectedFiles(prevFiles => [...prevFiles, ...filesToAdd])

      if (duplicateFiles.length > 0) {
        toast.error(`Duplicate files skipped: ${duplicateFiles.join(', ')}`, {
          duration: 5000
        })
      }

      if (uploadQuantity === 'single' && newFiles.length > 1) {
        toast.error('Multiple files are not allowed.')
      }

      // Prepare files for batch upload with individual abort controllers
      const filesToUpload: FileUpload[] = filesToAdd
        .filter(f => f.rawFile)
        .map(f => ({
          file: f.rawFile!,
          blobName: blobPrefix ? `${blobPrefix}/${f.path}` : f.path,
          abortController: f.abortController
        }))

      // Show initial toast to indicate upload has started
      if (filesToUpload.length > 0) {
        toast.loading(
          filesToUpload.length === 1
            ? 'Uploading file...'
            : `Uploading ${filesToUpload.length} files...`,
          { id: 'upload-batch', duration: Infinity }
        )
      }

      // Upload all files with concurrency control
      const uploadPromise = uploadMultipleFilesWithConcurrency(
        generateSasUrlUpload,
        uploadType,
        filesToUpload,
        aeroTempRunId,
        gmid,
        (filePath, pct) => {
          updateFileProgress(filePath, pct)
          onProgressUpdate?.(pct)
        }
      )

      // Expose the raw promise to the parent BEFORE chaining our own .then()
      // so callers can register background-finalize logic that outlives this component.
      if (onUploadPromiseCreated) {
        onUploadPromiseCreated(uploadPromise)
      }

      uploadPromise
        .then(results => {
          // Dismiss the loading toast
          toast.dismiss('upload-batch')

          const cancelledFiles = results.filter(
            r => !r.success && r.error === UploadStatus.CANCELLED
          )
          const failedFiles = results.filter(r => !r.success && r.error !== UploadStatus.CANCELLED)

          if (cancelledFiles.length > 0) {
            // Remove cancelled files from the list
            setSelectedFiles(prevFiles =>
              prevFiles.filter(file => !cancelledFiles.some(cu => cu.blobName.endsWith(file.path)))
            )
            cancelledFiles.forEach(file => {
              const fileName = file.blobName.split('/').pop() || file.blobName
              toast.success(`Upload cancelled: ${fileName}`, { duration: 3000 })
            })
          }

          if (failedFiles.length > 0) {
            // Mark failed files in the UI
            setSelectedFiles(prevFiles =>
              prevFiles.map(file => {
                const failed = failedFiles.some(fu => fu.blobName.endsWith(file.path))
                if (failed) {
                  return { ...file, failed: true, uploading: false, abortController: undefined }
                }
                return file
              })
            )

            failedFiles.forEach(failed => {
              const fileName = failed.blobName.split('/').pop() || failed.blobName
              console.error(`Upload failed for ${failed.blobName}:`, failed.error)
              toast.error(`Upload failed: ${fileName}`)
            })
          } else if (cancelledFiles.length === 0) {
            // All uploads completed successfully
            console.log('All uploads completed successfully')
            const successCount = results.filter(r => r.success).length
            if (successCount > 0) {
              toast.success(
                successCount === 1
                  ? 'File uploaded successfully'
                  : `${successCount} files uploaded successfully`,
                { duration: 3000 }
              )
            }
            // Clear abort controllers for successful uploads
            setSelectedFiles(prevFiles =>
              prevFiles.map(file => ({ ...file, abortController: undefined }))
            )
          }
        })
        .catch(err => {
          // Dismiss the loading toast
          toast.dismiss('upload-batch')
          console.error('Batch upload error', err)
          toast.error('Upload error occurred')
        })

      // Update form value with file paths
      const allFiles = [...selectedFiles, ...filesToAdd].map(f => f.name)
      if (uploadQuantity === 'single') {
        setValue(field.name, allFiles[0])
      } else {
        setValue(field.name, allFiles)
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleCancelFileUpload = (file: FileData) => {
    if (file.abortController) {
      file.abortController.abort()
    }
  }

  const removeFile = async (uploadType: string, blobName: string) => {
    const relativeBlobPath = blobPrefix ? `${blobPrefix}/${blobName}` : blobName
    const aeroTempRunId = isNew ? localStorage.getItem('aero_temp_run_id') : null
    return deleteFileDirectFromBlob(
      generateSasUrlDelete,
      uploadType,
      relativeBlobPath,
      aeroTempRunId,
      gmid
    )
  }

  const handleRemoveFile = (fileToRemove: FileData | null) => {
    if (!fileToRemove) {
      console.error('Cannot remove file: fileToRemove is null')
      return
    }

    const removeFileFromSelection = () => {
      const updatedFiles = selectedFiles.filter(f => f.path !== fileToRemove.path)
      setSelectedFiles(updatedFiles)
      setFileToDelete(null)
      setValue(
        field.name,
        updatedFiles.map(f => f.name)
      )
    }

    if (!deletePersistedFilesFromStorage) {
      removeFileFromSelection()
      return
    }

    removeFile(uploadType, fileToRemove.path)
      .then(() => {
        removeFileFromSelection()
      })
      .catch(err => {
        console.error('Direct delete failed', err)
        toast.error('Direct delete failed')
      })
  }

  const handleRemoveFailedFile = (fileToRemove: FileData) => {
    // Remove failed file from list without trying to delete from blob (since upload failed)
    const updatedFiles = selectedFiles.filter(f => f.path !== fileToRemove.path)
    setSelectedFiles(updatedFiles)
    // Update form value
    setValue(
      field.name,
      updatedFiles.map(f => f.name)
    )
    toast.success(`Removed failed upload: ${fileToRemove.name}`, { duration: 3000 })
  }

  const updateFileProgress = (blobPath: string, progress: number) => {
    setSelectedFiles(prevFiles =>
      prevFiles.map(file => {
        // Match by checking if blobPath ends with file.path (handles prefix)
        const matches = blobPath.endsWith(file.path)
        return matches ? { ...file, progress, uploading: progress < 100 } : file
      })
    )
    // Note: Don't show toast here - it gets called multiple times per file
    // Toast is shown in the batch completion handler instead
  }

  const handleFileAreaClick = () => {
    if (!disabled) fileInputRef.current?.click()
  }

  const handleDeleteFile = () => {
    if (!fileToDelete) {
      console.error('Cannot delete file: fileToDelete is null')
      handleModalClose()
      return
    }
    handleRemoveFile(fileToDelete)
    handleModalClose()
  }

  const handleModalClose = () => {
    setShowModal(false)
  }

  return (
    <div className={classNames('flex flex-col', { 'opacity-50 cursor-not-allowed': disabled })}>
      <Modal
        isOpen={showModal}
        title={`Are you sure you want to remove the file [${fileToDelete?.name}] from ${deleteConfirmationTarget}?`}
        withAction={false}
        showClose={false}
        onCancel={handleModalClose}
        zIndex={50}
        size="sm">
        {/* Confirmation for deleting file */}
        <div className="flex flex-col gap-4">
          {/* Create buttons delete and cancel */}
          <div className="flex justify-end gap-4">
            <Button
              label="Delete"
              severity="danger"
              className="text-white"
              onClick={() => handleDeleteFile()}
            />
            <Button severity="secondary" className="text-white" onClick={() => handleModalClose()}>
              <span>Cancel</span>
            </Button>
          </div>
        </div>
      </Modal>
      <label htmlFor={field.name} className="mb-2">
        {label}
      </label>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        {...(allowDirectorySelection ? { webkitdirectory: '', directory: '' } : {})}
        multiple={uploadQuantity === 'multiple'}
        className="hidden"
        onChange={handleFileSelection}
        disabled={disabled}
      />
      <div
        className={classNames(
          'min-h-[42px] max-h-[200px] overflow-y-auto px-3 py-0 border rounded-md',
          'dark:bg-neutral-80 dark:border-neutral-70',
          'flex flex-wrap gap-2 items-center dark:border-neutral-60 border-gray-400',
          'transition-colors duration-200',
          { 'border-error dark:border-error': fieldState?.error },
          { 'cursor-pointer': !disabled },
          { 'cursor-not-allowed': disabled }
        )}
        onClick={handleFileAreaClick}>
        {selectedFiles.length === 0 ? (
          <span className="text-gm-black dark:text-white">
            {allowDirectorySelection
              ? 'Click to select a directory...'
              : 'Click to select files...'}
          </span>
        ) : (
          selectedFiles.map((file, index) => (
            <div
              key={index}
              className={classNames(
                'relative inline-block text-white rounded-full text-sm overflow-hidden',
                {
                  'bg-gm-blue': !file.failed,
                  'bg-error': file.failed
                }
              )}
              onClick={e => e.stopPropagation()}>
              {/* Background fill acting as progress bar */}
              <div
                className={classNames(
                  'absolute inset-0 bg-gm-aqua-light transition-all duration-300 ease-out',
                  { 'opacity-0': !file.uploading && file.progress === 100 }
                )}
                style={{ width: `${file.progress || 0}%`, transformOrigin: 'left' }}
              />

              <div className="relative z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-transparent">
                <span className="max-w-[200px] truncate text-white">{file.name}</span>
                {file.uploading && !file.failed && (
                  <span className="text-sm opacity-80 text-white">
                    {Math.round(file.progress || 0)}%
                  </span>
                )}
                {file.failed && <span className="text-sm opacity-80 text-white">Failed</span>}

                {/* Cancel button for uploading files */}
                {!disabled && file.uploading && !file.failed && (
                  <IconButton
                    title={`Cancel Upload ${file.name}?`}
                    severity="warning"
                    additionalClassName=" !w-5 !h-5 !bg-warning"
                    icon={<CloseIcon className="w-4 h-4 fill-white" />}
                    onClick={e => {
                      if (!disabled) {
                        e.stopPropagation()
                        handleCancelFileUpload(file)
                      }
                    }}
                  />
                )}

                {/* Remove button for failed files */}
                {!disabled && file.failed && (
                  <IconButton
                    title={`Remove ${file.name}?`}
                    severity="danger"
                    additionalClassName=" !w-5 !h-5"
                    icon={<DeleteIcon className="w-3 h-3 fill-white" />}
                    onClick={e => {
                      if (!disabled) {
                        e.stopPropagation()
                        handleRemoveFailedFile(file)
                      }
                    }}
                  />
                )}

                {/* Delete button for completed files */}
                {!disabled && !file.uploading && !file.failed && file.progress === 100 && (
                  <IconButton
                    title={`Delete ${file.name}?`}
                    severity="danger"
                    additionalClassName=" !w-5 !h-5"
                    icon={<DeleteIcon className="w-3 h-3 fill-white" />}
                    onClick={e => {
                      if (!disabled) {
                        e.stopPropagation()
                        setFileToDelete(file)
                        setShowModal(true)
                      }
                    }}
                  />
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <InputText id={field.name} {...field} className="hidden" />
      {getFormErrorMessage(field.name)}
    </div>
  )
}

export default Upload
